import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa';
import { useSelector } from 'react-redux'

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Email, 2: Code + New Password
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const auth = useSelector((state) => state.auth);
  // Add password validation state
  const [passwordErrors, setPasswordErrors] = useState({
    length: true,
    specialChars: true
  });
  const [touchedPassword, setTouchedPassword] = useState(false);

  // Password validation effect
  useEffect(() => {
    if (newPassword) {
      const lengthValid = newPassword.length >= 8;
      
      // Validate for special characters
      const specialChars = newPassword.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g);
      const specialCharsCount = specialChars ? specialChars.join('').length : 0;
      const specialCharsValid = specialCharsCount >= 1;
      
      setPasswordErrors({
        length: !lengthValid,
        specialChars: !specialCharsValid
      });
    }
  }, [newPassword]);

  // Handle password change
  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (!touchedPassword) {
      setTouchedPassword(true);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const headers = { 
        'Content-Type': 'application/json',
        'Api-Name': 'auth-api',
      };
      
      // Add token to headers if available
      if (auth && auth.token) {
        headers['Authorization'] = `Bearer ${auth.token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/forgot-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (response.ok) {
        setStep(2);
        setSuccess('Verification code sent to your email');
      } else {
        setError(data.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      // Provide more meaningful error messages based on the error type
      if (error.message === 'Failed to fetch') {
        setError('Unable to connect to the server. Try again later.');
      } else if (error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
        setError('Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please try again later.');
      } else {
        setError('An error occurred while sending the verification code. Please try again.');
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    // Check password validation before submitting
    if (passwordErrors.length || passwordErrors.specialChars) {
      setError('Please fix password requirements before submitting');
      return;
    }

    try {
      const headers = { 
        'Content-Type': 'application/json',
        'Api-Name': 'auth-api',
      };
      
      // Add token to headers if available
      if (auth && auth.token) {
        headers['Authorization'] = `Bearer ${auth.token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/confirm-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, code, newPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess('Password reset successful. You can now login with your new password.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      // Provide more meaningful error messages based on the error type
      if (error.message === 'Failed to fetch') {
        setError('Unable to connect to the server. Please check your internet connection or try again later.');
      } else if (error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
        setError('Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please try again later.');
      } else if (error.message.includes('code')) {
        setError('Invalid verification code. Please check and try again.');
      } else if (error.message.includes('password')) {
        setError('Password does not meet requirements. Please use a stronger password.');
      } else {
        setError('An error occurred while resetting your password. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pri-dark to-sec-dark">
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-0 w-96 h-96 bg-pri-yellow rounded-full mix-blend-multiply filter blur-[128px] opacity-20"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          x: [-50, 0, -50],
          y: [-50, 0, -50],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-pri-green rounded-full mix-blend-multiply filter blur-[128px] opacity-20"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [90, 0, 90],
          x: [50, 0, 50],
          y: [50, 0, 50],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      />

      <div className="relative min-h-screen flex items-center justify-center py-2 sm:py-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-6 sm:space-y-8 -mt-8 sm:-mt-4"
        >
          {/* Logo and Title */}
          <div className="text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mx-auto w-16 sm:w-20 h-16 sm:h-20 relative"
            >
              <img 
                className="h-16 w-auto mx-auto" 
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`} 
                alt="Jingleez" 
              />
              <motion.div
                className="absolute inset-0 bg-soft-pink rounded-full opacity-20 blur-md"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.3, 0.2],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
            <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold text-soft-pink">
              Reset Password
            </h2>
            <p className="mt-2 text-sm text-pri-gray">
              Remember your password?{' '}
              <Link to="/login" className="font-medium text-soft-pink hover:text-mint-green transition-colors duration-300">
                Sign In
              </Link>
            </p>
          </div>

          {/* Form Container */}
          <motion.div
            className="relative overflow-hidden rounded-2xl p-8 
                     bg-gradient-to-br from-soft-pink/10 to-soft-pink/5 
                     backdrop-blur-sm shadow-lg border border-white/10
                     transform-gpu"
            style={{
              transform: 'perspective(1000px) rotateX(2deg)',
            }}
            layout
          >
            {/* Glass-like highlight effect at the top */}
            <div className="absolute inset-x-0 top-0 h-px bg-white/30"></div>
            <div className="absolute -left-10 -right-10 top-0 h-10 rounded-full blur-md bg-gradient-to-r from-soft-pink/20 to-soft-pink/5"></div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-6 bg-red-500/10 text-red-500 p-3 rounded-lg text-sm relative"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Success message */}
            <AnimatePresence>
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-6 bg-green-500/10 text-green-400 p-3 rounded-lg text-sm relative"
                >
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.form
                  key="email-form"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleRequestCode}
                  className="relative space-y-6"
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="h-5 w-5 text-soft-pink/70" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                      placeholder="Enter your email"
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-full shadow-sm text-pri-dark bg-mint-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-pink transition-colors duration-200"
                  >
                    Send Reset Code
                  </motion.button>
                </motion.form>
              ) : (
                <motion.form
                  key="reset-form"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleResetPassword}
                  className="relative space-y-6"
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="h-5 w-5 text-soft-pink/70" />
                    </div>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                      placeholder="Enter verification code"
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="h-5 w-5 text-soft-pink/70" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={handlePasswordChange}
                      required
                      className="block w-full pl-10 pr-10 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="h-5 w-5 text-soft-pink/70" />
                      ) : (
                        <FaEye className="h-5 w-5 text-soft-pink/70" />
                      )}
                    </button>
                  </div>

                  {/* Password strength indicators */}
                  {touchedPassword && (
                    <div className="text-xs space-y-1 bg-pri-dark/30 p-3 rounded-lg">
                      <p className="text-pri-gray font-medium mb-1">Password requirements:</p>
                      <div className="flex items-center">
                        {passwordErrors.length ? (
                          <FaTimes className="text-red-500 mr-2" />
                        ) : (
                          <FaCheck className="text-green-500 mr-2" />
                        )}
                        <span className={passwordErrors.length ? "text-red-400" : "text-green-400"}>
                          At least 8 characters
                        </span>
                      </div>
                      <div className="flex items-center">
                        {passwordErrors.specialChars ? (
                          <FaTimes className="text-red-500 mr-2" />
                        ) : (
                          <FaCheck className="text-green-500 mr-2" />
                        )}
                        <span className={passwordErrors.specialChars ? "text-red-400" : "text-green-400"}>
                          At least 1 special characters
                        </span>
                      </div>
                    </div>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full mt-6 flex justify-center items-center px-4 py-3 border border-transparent rounded-full shadow-sm text-pri-dark bg-mint-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-pink transition-colors duration-200"
                  >
                    Reset Password
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
